import { useCallback } from 'react'
import { useQuery, useQueryClient, QueryFunction } from '@tanstack/react-query'
import { getTemplate } from '@global/api'
import { ApiResponseTemplate } from '@templates/types'
import { ACTION_TYPE } from './reducer'

const fetchTemplateColumns: QueryFunction<ApiResponseTemplate | null, [string, string]> = async ({
  queryKey,
  signal,
}) => {
  const [_key, templateId] = queryKey
  return getTemplate({
    templateID: templateId,
    signal,
  })
}

/**
 * Fetch template columns for a given template ID, and update external state with the selected template ID.
 * Refetch is used independently since the selectTemplateId changes and is initially disabling the query.
 * @param param0
 * @returns
 */
export const useTemplateColumnsQuery = ({ selectedTemplateId, dispatch }) => {
  const queryClient = useQueryClient()

  const queryResult = useQuery({
    queryKey: ['templateColumns', selectedTemplateId],
    queryFn: fetchTemplateColumns,
    enabled: Boolean(selectedTemplateId),
    refetchOnWindowFocus: false,
  })

  const refetchWithTemplateID = useCallback(
    async (id: string) => {
      await queryClient.invalidateQueries({ queryKey: ['templateColumns', id] })
      await queryClient.prefetchQuery({
        queryKey: ['templateColumns', id],
        queryFn: fetchTemplateColumns,
      })

      dispatch({ type: ACTION_TYPE.setTemplateSelection, payload: id })
    },
    [queryResult.refetch, selectedTemplateId],
  )

  // React Query v4 has isLoading set to true when query is not enabled by default.
  // We need to fix this behavior to understand if the query is truly in a loading state.
  const isLoading = queryResult.isLoading && queryResult.isFetching

  return {
    ...queryResult,
    isLoading,
    refetchWithTemplateID,
  }
}
